'use client';

import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  COMMUNITY_TOKENS,
  CommunityAvatar,
  CommunityChip
} from './CommunityNotificationCard';
import { formatNotificationTime } from './communityNotifications';

type CommunityBundleCardProps = {
  /** Community notifications folded into this card. */
  count: number;
  /** The newest of them, for the timestamp. */
  latestAt: string;
  hasUnread: boolean;
  onOpen: () => void;
};

/**
 * SB-1912 — "You have N more Community notifications". Stands in for the
 * community items past the first few in the All tab so they do not crowd out
 * admissions and scholarships; opening it shows them all in the Community tab.
 */
const CommunityBundleCard: React.FC<CommunityBundleCardProps> = ({
  count,
  latestAt,
  hasUnread,
  onOpen
}) => (
  <Box
    component="button"
    type="button"
    onClick={onOpen}
    aria-label={`View ${count} more community notifications`}
    sx={styles.card}
  >
    {hasUnread && <Box sx={styles.dot} />}
    <CommunityAvatar label={`+${count}`} />

    <Stack spacing={0.5} flex={1} minWidth={0} alignItems="flex-start">
      <CommunityChip label="Community bundle" />
      <Typography sx={styles.title}>
        You have {count} more Community{' '}
        {count === 1 ? 'notification' : 'notifications'}
      </Typography>
      <Typography sx={styles.hint}>
        Tap to view all mentions, comments, and replies.
      </Typography>
    </Stack>

    <Stack alignItems="flex-end" justifyContent="space-between" gap="12px">
      <Typography sx={styles.time}>
        {formatNotificationTime(latestAt)}
      </Typography>
      <Typography sx={styles.arrow} aria-hidden>
        →
      </Typography>
    </Stack>
  </Box>
);

const styles = {
  card: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    backgroundColor: COMMUNITY_TOKENS.bundleBg,
    transition: 'background-color 0.2s',
    '&:hover': { backgroundColor: COMMUNITY_TOKENS.amberFill },
    '&:focus-visible': {
      outline: `2px solid ${COMMUNITY_TOKENS.amberDot}`,
      outlineOffset: '-2px'
    }
  },
  dot: {
    position: 'absolute',
    left: 6,
    top: '50%',
    translate: '0 -50%',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: COMMUNITY_TOKENS.amberDot
  },
  title: {
    pt: '4px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '24px',
    color: COMMUNITY_TOKENS.title
  },
  hint: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: COMMUNITY_TOKENS.body
  },
  time: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '16px',
    color: COMMUNITY_TOKENS.time,
    whiteSpace: 'nowrap'
  },
  arrow: {
    fontSize: '20px',
    lineHeight: '28px',
    color: COMMUNITY_TOKENS.amberText
  }
} as const;

export default CommunityBundleCard;
